// src/components/AllClassesSchedulePane.jsx
// すべての授業の授業スケジュール【2026-10-05】
//
//   「すべての授業」を選んだときの授業スケジュール。上の期間（PeriodBar）の担当授業ぶん。
//   見せ方は2つ（切り替えはこのブラウザに覚える）:
//     ・授業ごと … 1つの授業の「授業の回」（UpcomingSessions）を授業の数だけ縦に並べる。
//                  授業名の見出しで開け閉めできる（閉じた授業もこのブラウザに覚える）。
//                  休講・代講もここから入れられる
//     ・日付順   … 全部の授業の回を日付順に1本の一覧にする（見るだけ。授業名から各授業へ）
//   休み・休講の日はどちらもたたんでおく（UpcomingSessions と同じ決まり・同じ覚え方）。
//   サーバーは授業ごとの GET /api/schedule/classes/:id/upcoming?scope=term をそのまま使う。
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import UpcomingSessions, { DAY, STATE, foldOffDays, isOffDay, readHideOff, saveHideOff, FoldRow } from './UpcomingSessions';

const MODE_KEY = 'allSchedule.mode';
const CLOSED_KEY = 'allSchedule.closed';
const readMode = () => { try { return localStorage.getItem(MODE_KEY) === 'date' ? 'date' : 'class'; } catch (e) { return 'class'; } };
const readClosed = () => {
  try { return new Set(JSON.parse(localStorage.getItem(CLOSED_KEY) || '[]').map(String)); } catch (e) { return new Set(); }
};
const md = (date) => { const [, m, d] = date.split('-'); return `${Number(m)}/${Number(d)}`; };

export default function AllClassesSchedulePane({ classes, auth, setErr, setMsg }) {
  const [mode, setModeState] = useState(readMode);
  const setMode = (v) => { setModeState(v); try { localStorage.setItem(MODE_KEY, v); } catch (e) { /* 覚えられなくても動く */ } };
  const [closed, setClosedState] = useState(readClosed);
  const toggle = (id) => {
    const next = new Set(closed);
    if (next.has(String(id))) next.delete(String(id)); else next.add(String(id));
    setClosedState(next);
    try { localStorage.setItem(CLOSED_KEY, JSON.stringify([...next])); } catch (e) { /* 覚えられなくても動く */ }
  };
  // ほかの授業にまとめた授業は、まとめ先で見るので出さない
  const list = (classes || []).filter(c => !c.linked_to_class_id);

  return (
    <>
      <div className="card" style={{ padding: '0.6rem 1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
          <strong style={{ flex: 1 }}>すべての授業の授業スケジュール（{list.length}授業）</strong>
          <div style={{ display: 'flex', gap: 0 }}>
            {[['class', '授業ごと'], ['date', '日付順']].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setMode(v)} className={mode === v ? 'btn-primary' : 'btn-secondary'}
                      style={{ padding: '0.2rem 0.7rem', fontSize: '0.85rem' }}>{l}</button>
            ))}
          </div>
          {mode === 'class' && list.length > 0 && (
            <>
              <button type="button" className="btn-secondary" style={{ padding: '0.2rem 0.6rem', fontSize: '0.8rem' }}
                      onClick={() => { setClosedState(new Set()); try { localStorage.setItem(CLOSED_KEY, '[]'); } catch (e) { /* 覚えられなくても動く */ } }}>
                すべて開く
              </button>
              <button type="button" className="btn-secondary" style={{ padding: '0.2rem 0.6rem', fontSize: '0.8rem' }}
                      onClick={() => {
                        const all = new Set(list.map(c => String(c.id)));
                        setClosedState(all);
                        try { localStorage.setItem(CLOSED_KEY, JSON.stringify([...all])); } catch (e) { /* 覚えられなくても動く */ }
                      }}>
                すべて閉じる
              </button>
            </>
          )}
        </div>
      </div>

      {list.length === 0 ? (
        <div className="card"><p style={{ color: '#6b7280' }}>この期間の担当授業はありません。</p></div>
      ) : mode === 'class' ? (
        list.map(c => {
          const isClosed = closed.has(String(c.id));
          return (
            <div key={c.id} style={{ marginBottom: '0.6rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0.2rem' }}>
                <button type="button" onClick={() => toggle(c.id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', fontWeight: 700, padding: 0, textAlign: 'left' }}>
                  {isClosed ? '▸' : '▾'} {c.name}{c.semester ? <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '0.85rem' }}>（{c.semester}）</span> : null}
                </button>
                <Link to={'/classes/' + c.id + '/schedule'} style={{ fontSize: '0.8rem' }}>この授業を開く →</Link>
              </div>
              {!isClosed && <UpcomingSessions classId={c.id} auth={auth} setErr={setErr} setMsg={setMsg} />}
            </div>
          );
        })
      ) : (
        <ByDate classes={list} auth={auth} setErr={setErr} />
      )}
    </>
  );
}

// 日付順：全部の授業の回を1本の一覧にする（見るだけ）
function ByDate({ classes, auth, setErr }) {
  const [rows, setRows] = useState(null);
  const [failed, setFailed] = useState([]);
  const [today, setToday] = useState(null);
  const [hideOff, setHideOffState] = useState(readHideOff);
  const setHideOff = (v) => { setHideOffState(v); saveHideOff(v); };
  const key = classes.map(c => c.id).join(',');

  useEffect(() => {
    let alive = true;
    setRows(null);
    (async () => {
      const results = await Promise.all(classes.map(async (c) => {
        try {
          const r = await fetch('/api/schedule/classes/' + c.id + '/upcoming?scope=term', { headers: auth() });
          const d = await r.json();
          if (!r.ok || !d.success) throw new Error(d.message || 'error');
          return { c, d };
        } catch (e) { return { c, error: true }; }
      }));
      if (!alive) return;
      const all = [];
      let t = null;
      for (const x of results) {
        if (x.error) continue;
        t = t || x.d.today;
        for (const it of (x.d.items || [])) all.push({ ...it, classId: x.c.id, className: x.c.name });
      }
      all.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : String(a.start || '').localeCompare(String(b.start || ''))));
      setFailed(results.filter(x => x.error).map(x => x.c.name));
      setToday(t);
      setRows(all);
    })().catch(e => { if (alive) { setErr(e.message); setRows([]); } });
    return () => { alive = false; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!rows) return <div className="card"><p>読み込み中...</p></div>;
  const offCount = new Set(rows.filter(isOffDay).map(it => it.date + ':' + it.classId)).size;
  const entries = foldOffDays(rows, hideOff);
  const visible = entries.filter(e => !e.fold).map(e => e.it);

  return (
    <div className="card">
      <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <h2 style={{ margin: 0, flex: 1 }}>日付順</h2>
        {(offCount > 0 || !hideOff) && (
          <button type="button" className="btn-secondary" onClick={() => setHideOff(!hideOff)}
                  style={{ padding: '0.15rem 0.6rem', fontSize: '0.8rem' }}>
            {hideOff ? `休み・休講も表示（${offCount}件）` : '休み・休講をたたむ'}
          </button>
        )}
      </div>
      {failed.length > 0 && (
        <p style={{ color: '#b91c1c', fontSize: '0.85rem' }}>読み込めなかった授業: {failed.join('、')}</p>
      )}
      <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: 0 }}>
        曜日時限と学期・大学の授業カレンダーから計算しています。休講・代講は「授業ごと」か各授業の画面から入れてください。
      </p>
      {rows.length === 0 ? (
        <p style={{ color: '#888' }}>授業の回はありません（曜日時限と学期が未設定です）。</p>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>日付</th><th>時刻</th><th>授業</th><th>回</th><th>教室</th><th>状態</th></tr></thead>
            <tbody>{entries.map((e, ei) => {
              if (e.fold) return <FoldRow key={'fold' + ei + ':' + e.items[0].date} items={e.items} colSpan={6} onOpen={() => setHideOff(false)} />;
              const it = e.it;
              const i = visible.indexOf(it);
              const st = STATE[it.state] || STATE.planned;
              const firstOfDate = i === 0 || visible[i - 1].date !== it.date;
              const past = today && it.date < today;
              const isToday = today && it.date === today;
              return (
                <tr key={it.classId + ':' + it.date + ':' + (it.scheduleId || it.sessionId || ei)}
                    style={{
                      ...(it.state === 'cancelled' || it.state === 'closed' ? { color: '#9ca3af' } : past ? { color: '#6b7280' } : {}),
                      ...(it.flag ? { background: '#fef2f2' } : isToday ? { background: '#eff6ff' } : {}),
                      ...(firstOfDate && i > 0 ? { borderTop: '2px solid #e5e7eb' } : {}),
                    }}>
                  <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                    {firstOfDate ? `${md(it.date)}（${DAY[it.dayOfWeek]}）` : ''}
                    {firstOfDate && isToday && <span style={{ marginLeft: '0.3rem', fontSize: '0.75rem', color: '#1d4ed8' }}>今日</span>}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{it.start}{it.end ? `〜${it.end}` : ''}</td>
                  <td><Link to={'/classes/' + it.classId + '/schedule'}>{it.className}</Link></td>
                  <td style={{ whiteSpace: 'nowrap' }}>{it.no ? `第${it.no}回` : ''}</td>
                  <td>{it.room || <span style={{ color: '#9ca3af' }}>未設定</span>}</td>
                  <td>
                    <span style={{ background: st.bg, color: st.color, borderRadius: 999, padding: '0.05rem 0.5rem',
                                   fontSize: '0.78rem', fontWeight: 700 }}>{st.label}</span>
                    {it.state === 'substitute' && it.substituteName && <span style={{ marginLeft: '0.35rem', fontSize: '0.82rem' }}>{it.substituteName} 先生</span>}
                    {it.reason && <span style={{ marginLeft: '0.35rem', fontSize: '0.8rem', color: '#6b7280' }}>（{it.reason}）</span>}
                    {it.swappedFrom != null && <span style={{ marginLeft: '0.35rem', fontSize: '0.78rem', color: '#b45309', fontWeight: 700 }}>振替（{DAY[it.swappedFrom]}曜の授業）</span>}
                    {it.flag && <span style={{ marginLeft: '0.35rem', fontSize: '0.78rem', color: '#b91c1c', fontWeight: 700 }}>⚠ {it.flag}</span>}
                  </td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
